<template>
    <b-collapse :id="`collapse-expectation-checkins-${expectationId}`">
        <div class="mb-4">
            <div
                class="bg-light change-cursor-to-pointer list-group-item-accent-secondary ml-4 mr-4 mt-1 mb-2 p-1"
                align-self="center"
                v-b-toggle="`checkins-expectation-${expectationId}`"
            >
                <div class="text-center font-weight-bold text-muted small">
                    {{ $t('calculation.checkIns') }}
                </div>
            </div>
            <b-collapse
                :id="`checkins-expectation-${expectationId}`"
                :visible="true"
            >
                <b-container v-if="checkins.length">
                    <b-row
                        class="ml-2 list-group-item-accent-light"
                        :key="checkin.id"
                        v-for="checkin in checkins.slice().reverse()"
                    >
                        <b-col lg="5" cols="10" class="align-self-center">
                            <small class="text-muted">
                                <i class="icon-calendar mr-1"></i>
                                {{ getDate(checkin.created_at) }}
                            </small>
                        </b-col>

                        <b-col lg="1" cols="1" order-lg="2">
                            <b-link
                                id="btn-target-check-in-edit"
                                class="btn-check-in"
                                :disabled="canEditCheckIn"
                                @click="editCheckin(checkin.id)"
                            >
                                <i
                                    class="fa fa-pencil text-primary"
                                    :class="{ 'not-allowed': canEditCheckIn }"
                                ></i>
                            </b-link>
                        </b-col>

                        <b-col lg="5" cols="10" order-lg="1">
                            <small class="text-muted">
                                <i class="icon-graph mr-1"></i>
                                {{ $t('profile.table.result') }}
                                {{
                                    checkin.current_value
                                        ? formatNumber(checkin.current_value)
                                        : '-'
                                }}
                            </small>
                        </b-col>

                        <b-col lg="1" cols="1" order-lg="3">
                            <b-link
                                id="btn-target-delete-check-in"
                                class="btn-check-in"
                                :disabled="canEditCheckIn"
                                @click="deleteCheckin(checkin.id)"
                            >
                                <i
                                    class="fa fa-trash text-danger"
                                    :class="{ 'not-allowed': canEditCheckIn }"
                                ></i>
                            </b-link>
                        </b-col>
                    </b-row>
                </b-container>
                <b-alert
                    show
                    v-else
                    class="mx-4 p-1 text-center text-muted font-weight-bold small"
                >
                    {{ $t('profile.table.details.expectationHasNoCheckin') }}
                </b-alert>
            </b-collapse>
        </div>
        <div>
            <div
                class="bg-light change-cursor-to-pointer list-group-item-accent-secondary ml-4 mr-4 mt-1 mb-2 p-1"
                align-self="center"
                v-b-toggle="`simulated-checkins-expectation-${expectationId}`"
            >
                <div class="text-center font-weight-bold text-muted small">
                    {{ $t('checkIn.checkinsForecast') }}
                </div>
            </div>
            <b-collapse
                :id="`simulated-checkins-expectation-${expectationId}`"
                :visible="true"
            >
                <b-container v-if="simulatedCheckins.length">
                    <b-row
                        class="ml-2 list-group-item-accent-light"
                        :key="checkin.id"
                        v-for="checkin in simulatedCheckins"
                    >
                        <b-col lg="5" cols="10" class="align-self-center">
                            <div class="text-muted">
                                <small>
                                    <i class="icon-calendar mr-1"></i>
                                    {{ getDate(checkin.created_at) }}
                                </small>
                            </div>
                        </b-col>

                        <b-col lg="1" cols="1" order-lg="2">
                            <b-link
                                id="btn-target-edit-simulated-check-in"
                                class="btn-check-in"
                                :disabled="canEditCheckIn"
                                @click="editCheckinSimulated(checkin.id)"
                            >
                                <i
                                    class="fa fa-pencil text-primary"
                                    :class="{ 'not-allowed': canEditCheckIn }"
                                ></i>
                            </b-link>
                        </b-col>

                        <b-col lg="5" cols="10" order-lg="1">
                            <div class="text-muted">
                                <small>
                                    <i class="icon-graph mr-1"></i>
                                    {{ $t('form.label.forecast') }}
                                    {{ formatNumber(checkin.current_value) }}
                                </small>
                            </div>
                        </b-col>

                        <b-col lg="1" cols="1" order-lg="3">
                            <b-link
                                id="btn-target-another-delete-check-in"
                                class="btn-check-in"
                                :disabled="canEditCheckIn"
                                @click="deleteCheckin(checkin.id)"
                            >
                                <i
                                    class="fa fa-trash text-danger"
                                    :class="{ 'not-allowed': canEditCheckIn }"
                                ></i>
                            </b-link>
                        </b-col>
                    </b-row>
                </b-container>
                <b-alert
                    show
                    v-else
                    class="mx-4 p-1 text-center text-muted font-weight-bold small"
                >
                    {{ $t('checkIn.expectationHasNoCheckinForecast') }}
                </b-alert>
            </b-collapse>
        </div>
    </b-collapse>
</template>

<script>
import api from '@/services/api'
import Swal from 'sweetalert2'
import i18n from '@/i18n'
import notification, { success, danger } from '@/lib/notification'
import { deleteConfirmationWithLoading } from '@/config/sweet-alert'
import { formatNumber } from '@/lib/utils'
import { getStringFromDateWithoutTime } from '@/lib/date'

notification.init()

export default {
    name: 'CheckinsList',
    props: [
        'expectationId',
        'checkins',
        'simulatedCheckins',
        'indicatorType',
        'indicatorId',
        'indicatorLevel',
        'currentEventPermissions',
        'isIndicatorOwnerOrManager',
        'isAdmin',
        'isAllowedToEdit',
        'canEditCheckIn',
        'isFocalPoint',
    ],
    methods: {
        editCheckin(checkinId) {
            if (this.indicatorType == 'target') {
                this.targetCheckinModal(this.indicatorId, checkinId)
            } else if (this.indicatorType == 'kpi') {
                this.kpiCheckinModal(this.indicatorId, checkinId)
            }
        },
        deleteCheckin(checkinId) {
            if (this.indicatorType == 'target') {
                this.targetDeleteCheckin(this.indicatorId, checkinId)
            } else if (this.indicatorType == 'kpi') {
                this.kpiDeleteCheckin(this.indicatorId, checkinId)
            }
        },
        editCheckinSimulated(checkinId) {
            if (this.indicatorType == 'target') {
                this.targetCheckinSimulatedModal(this.indicatorId, checkinId)
            } else if (this.indicatorType == 'kpi') {
                this.kpiCheckinSimulatedModal(this.indicatorId, checkinId)
            }
        },
        targetCheckinModal(indicatorId, checkinId) {
            if (this.userHasPermission()) {
                return this.$router.push({
                    path: `/v2/target/targets/view/${indicatorId}/checkin/${checkinId}`,
                })
            }
            this.editPermissionDeniedSwal()
        },
        kpiCheckinModal(indicatorId, checkinId) {
            if (this.userHasPermission()) {
                return this.$router.push({
                    path: `/v2/target/indicators/view/${indicatorId}/checkin/${checkinId}`,
                })
            }
            this.editPermissionDeniedSwal()
        },
        targetCheckinSimulatedModal(indicatorId, checkinId) {
            if (this.userHasPermission()) {
                return this.$router.push({
                    path: `/v2/target/targets/view/${indicatorId}/checkin-projection/${checkinId}`,
                })
            }
            this.editPermissionDeniedSwal()
        },
        kpiCheckinSimulatedModal(indicatorId, checkinId) {
            if (this.userHasPermission()) {
                return this.$router.push({
                    path: `/v2/target/indicators/view/${indicatorId}/checkin-projection/${checkinId}`,
                })
            }
            this.editPermissionDeniedSwal()
        },
        targetDeleteCheckin(indicatorId, checkinId) {
            if (this.userHasPermission()) {
                return Swal({
                    ...deleteConfirmationWithLoading,
                    preConfirm: () => {
                        return api
                            .delete(
                                `goals/${indicatorId}/checkins/${checkinId}`
                            )
                            .catch(console.error)
                    },
                })
                    .then(this.notificationSuccessfulDelete)
                    .catch((e) => {
                        console.error(e)
                        this.notificationError()
                        this.$router.back()
                    })
            }
            this.deletePermissionDeniedSwal()
        },
        kpiDeleteCheckin(indicatorId, checkinId) {
            if (this.userHasPermission()) {
                return Swal({
                    ...deleteConfirmationWithLoading,
                    preConfirm: () => {
                        return api
                            .delete(`kpi/${indicatorId}/checkins/${checkinId}`)
                            .catch(console.error)
                    },
                })
                    .then(this.notificationSuccessfulDelete)
                    .catch((e) => {
                        console.error(e)
                        this.notificationError()
                        this.$router.back()
                    })
            }
            this.deletePermissionDeniedSwal()
        },
        userHasPermission() {
            if (this.isAdmin) return true
            if (this.isIndicatorOwnerOrManager || this.isFocalPoint) {
                return this.currentEventPermissions.goals
                    ? this.currentEventPermissions.goals[this.indicatorLevel]
                          .edit_checkin
                    : false
            }
            return false
        },
        notificationError() {
            danger()
        },
        formatNumber(number) {
            return formatNumber(number)
        },
        getDate(date) {
            return getStringFromDateWithoutTime(date, 'YYYY-MM-DD')
        },
        deletePermissionDeniedSwal() {
            Swal({
                type: 'warning',
                title: i18n.t('notification.permissions.deleteDenied', {
                    item: i18n.t('checkIn.checkIn'),
                }),
            })
        },
        editPermissionDeniedSwal() {
            Swal({
                type: 'warning',
                title: i18n.t('notification.permissions.editDenied', {
                    item: i18n.t('checkIn.checkIn'),
                }),
            })
        },
        notificationSuccessfulDelete(resp) {
            const { value } = resp
            if (value) {
                success(
                    i18n.t('notification.deletedSuccessfully', {
                        item: i18n.t('checkIn.checkIn'),
                    })
                )
                this.$emit('update-parent', 'updateCheckin')
            }
        },
    },
}
</script>
<style scoped>
.not-allowed {
    cursor: not-allowed;
}
</style>
